import { z } from "zod";
// No "user_confirmed" — Ville ruled out a Glide-to-relay callback (plan
// 0.3/0.4), so there's no step waiting on Glide to confirm a write.
const orderStep = z.enum(["user_upsert", "sale_rows", "email"]);
const authMethod = z.enum(["ftn", "google", "fake"]);
const userRole = z.enum(["client", "staff", "partner"]);
const signinOutcome = z.enum(["unsupported", "no_credential", "cancelled", "failed"]);
// Why the passkey enrolment prompt was shown. "new_device" is the case the
// account-level check used to miss entirely: the user has a passkey, just not
// on the device in front of them.
const enrolmentReason = z.enum(["no_passkey", "new_device", "check_failed"]);
const chatAuthorType = z.enum(["client", "staff", "assistant", "system"]);
const chatThreadStatus = z.enum(["open", "resolved"]);
const actionKind = z.enum(["supplement", "diet", "lab", "visit"]);
const signOutSurface = z.enum(["nav_rail", "account_sheet"]);
// One per question the onboarding flow asks, plus the intro that carries the
// name confirmation and the non-prod skip.
const onboardingStep = z.enum([
    "intro",
    "name",
    "sex",
    "age",
    "life_stage",
    "location",
    "email",
    "consents",
]);
const installPlatform = z.enum(["ios", "android", "other"]);
const displayMode = z.enum(["standalone", "browser", "minimal-ui", "fullscreen"]);
const symptomCheckinKind = z.enum(["quick", "full"]);
const orderSource = z.enum(["storefront", "client_app", "woocommerce", "glide_legacy", "event_pos"]);
const cartAction = z.enum(["add", "remove", "set_quantity", "clear"]);
const ctaAction = z.enum(["measure", "help", "learn_more", "join"]);
// The client PWA's bottom-bar destinations, by route path. Exported so
// apps/client/src/app/routes.ts can type its own tab list against this and a
// new tab cannot be added to the bar without the catalog learning about it.
const clientTab = z.enum(["/", "/infusion", "/support", "/results", "/chat"]);
// How a tab change was initiated. "back" is the hardware back button or the
// iOS edge swipe; "in_app_link" is anything that navigates to a tab from
// outside the bar (today: the home surface's chat blocks); "nav_rail" is the
// persistent desktop sidebar that replaces the pill above 960px.
const tabChangeMethod = z.enum(["tab_bar", "back", "in_app_link", "nav_rail"]);
// The reader's collapsible sections by fixed id. Never a question key, so a
// toggle cannot reveal which answers a client has.
const summarySectionId = z.enum([
    "pvk_accordion",
    "symptoms",
    "conditions",
    "menstruation",
    "pregnancy_contraception",
    "diet",
    "gut",
    "iron_supplements",
    "other_supplements",
    "iron_infusions",
]);
export const catalog = {
    // The seven orders.* events below have no emitter since the WooCommerce
    // order relay was retired (2026-09-30). Their schemas stay because the
    // rows they produced are still in BigQuery, and this catalog is where
    // someone reading a historical row looks up what the fields meant. Do not
    // add new emitters -- the Stripe path has its own commerce.* events.
    "orders.webhook_received": z.object({
        order_id: z.string(),
        woo_customer_id: z.string().optional(),
        total: z.number(),
        currency: z.string(),
    }),
    "orders.claimed": z.object({
        order_id: z.string(),
    }),
    "orders.step_completed": z.object({
        order_id: z.string(),
        step: orderStep,
        line_item_id: z.string().optional(),
    }),
    "orders.email_sent": z.object({
        order_id: z.string(),
        email_domain_class: z.enum(["consumer", "work"]),
    }),
    "orders.email_skipped": z.object({
        order_id: z.string(),
        reason: z.string(),
    }),
    "orders.processing_failed": z.object({
        order_id: z.string(),
        step: orderStep,
        error: z.string(),
    }),
    "orders.dead_lettered": z.object({
        order_id: z.string(),
        step: orderStep,
    }),
    "mailerlite.webhook_received": z.object({
        subscriber_id: z.string(),
        group_id: z.string(),
        group_name: z.string(),
    }),
    "mailerlite.signin_email_sent": z.object({
        subscriber_id: z.string(),
        group_id: z.string(),
        email_domain_class: z.enum(["consumer", "work"]),
    }),
    "mailerlite.processing_failed": z.object({
        subscriber_id: z.string(),
        group_id: z.string(),
        error: z.string(),
    }),
    "stripe.webhook_received": z.object({
        session_id: z.string(),
        client_reference_id: z.string(),
        amount_total: z.number(),
        currency: z.string(),
        labs: z.array(z.enum(["pvk", "vitd"])),
    }),
    "stripe.checkout_synced": z.object({
        session_id: z.string(),
        email_domain_class: z.enum(["consumer", "work"]),
    }),
    "stripe.checkout_ignored": z.object({
        session_id: z.string(),
        reason: z.string(),
    }),
    "stripe.processing_failed": z.object({
        session_id: z.string(),
        error: z.string(),
    }),
    // No HETU, no names — identities.given_name/family_name and any national
    // ID must never reach the event catalog.
    "auth.eid_started": z.object({
        method: authMethod,
    }),
    "auth.eid_completed": z.object({
        user_id: z.string(),
        method: authMethod,
        is_new_user: z.boolean(),
    }),
    "auth.eid_failed": z.object({
        method: authMethod,
        // Fixed classification strings emitted by apps/api/src/modules/auth/service.ts:
        // "invalid_state", "decryption_failed" (JWE decrypt failed), "par_failed"
        // (PAR/authorization request failed), "provider_error" (everything else).
        // Left as z.string() rather than an enum since other emitters may add reasons.
        reason: z.string(),
    }),
    "auth.session_revoked": z.object({
        user_id: z.string(),
    }),
    // US email-OTP sign-in. Never the email, the code or a name: `outcome`
    // deliberately does not say whether an account exists.
    "auth.email_otp_requested": z.object({
        outcome: z.enum(["sent", "rate_limited", "not_allowlisted"]),
    }),
    "auth.email_otp_verified": z.object({
        user_id: z.string(),
        is_new_user: z.boolean(),
    }),
    "auth.email_otp_failed": z.object({
        reason: z.enum(["invalid_code", "expired", "too_many_attempts"]),
    }),
    // US only: an OIDC identity the deployment refused before any user row was
    // created (a non-staff Google account).
    "auth.eid_refused": z.object({
        method: authMethod,
        reason: z.enum(["email_unverified", "domain_not_permitted"]),
    }),
    "auth.passkey_registered": z.object({
        user_id: z.string(),
    }),
    "auth.passkey_login_succeeded": z.object({
        user_id: z.string(),
    }),
    "auth.passkey_login_failed": z.object({
        reason: z.string(),
    }),
    // The asiakastietolaki 9 § register of users and their access rights.
    // No justification text here — free text is exactly the kind of field
    // the no-PII rule is meant to keep out of the event catalog.
    "auth.access_granted": z.object({
        user_id: z.string(),
        role: userRole,
        granted_by: z.string(),
    }),
    "auth.access_revoked": z.object({
        user_id: z.string(),
        role: userRole,
        revoked_by: z.string(),
    }),
    // Browser-reported sign-in funnel, deliberately kept distinct from the
    // server-confirmed auth.* events above — a client can report "screen
    // viewed" or "passkey abandoned" without the server ever knowing.
    "signin.screen_viewed": z.object({}),
    "signin.started": z.object({}),
    // `allowlisted`: did the browser name the credential it already uses, or
    // fall back to the platform's "which passkey?" picker? On both events, so
    // the abandonment rate can be read per branch — which is the number that
    // would show a stale allowlist stranding people on bank ID. Rows emitted
    // before the allowlist shipped carry neither.
    "signin.passkey_attempted": z.object({ allowlisted: z.boolean() }),
    "signin.passkey_abandoned": z.object({ outcome: signinOutcome, allowlisted: z.boolean() }),
    "signin.eid_redirected": z.object({}),
    "signin.otp_requested": z.object({}),
    "signin.otp_code_submitted": z.object({}),
    "signin.otp_resent": z.object({}),
    "signin.otp_failed": z.object({ code: z.string() }),
    "signin.failed": z.object({ code: z.string() }),
    "signin.callback_completed": z.object({}),
    "signin.enrolment_shown": z.object({ reason: enrolmentReason }),
    "signin.enrolment_accepted": z.object({}),
    "signin.enrolment_skipped": z.object({}),
    // The device already held a passkey for this account (the authenticator
    // refused registrationOptions' excludeCredentials), so the prompt resolved
    // itself without enrolling anything.
    "signin.enrolment_already_on_device": z.object({}),
    // The account-level passkey check failed, so the prompt fell back to what
    // the browser knows on its own. Silent before, which is how a check that
    // suppressed the prompt could not be seen failing.
    "signin.enrolment_check_failed": z.object({}),
    // Not the asiakastietolaki 10 § käyttöloki (client-data access log) — that
    // is a separate access_log (apps/api/src/modules/access-log), written by
    // the data-access layer. UUIDs only, per the no-PII rule above.
    "clinic.patient_list_viewed": z.object({
        viewed_by: z.string(),
        count: z.number(),
        limit: z.number(),
        has_cursor: z.boolean(),
        track: z.string().nullable(),
    }),
    "clinic.patient_opened": z.object({
        viewed_by: z.string(),
        patient_id: z.string(),
    }),
    "clinic.patient_timeline_viewed": z.object({
        viewed_by: z.string(),
        patient_id: z.string(),
        entry_count: z.number(),
    }),
    // The Details pane's read of a customer's onboarding answers — this is
    // the access_log("customer_profile") disclosure surfaced as an event.
    "clinic.patient_details_viewed": z.object({
        patient_id: z.string().uuid(),
    }),
    // The Labs tab's read of a customer's measured results — the
    // access_log("patient_results") disclosure surfaced as an event. Draw count
    // only: no value, no unit, no analyte code, no collection date, matching
    // the no-clinical-content rule results.viewed already follows on the client
    // side (docs/analytics-and-automation.md §3/§6).
    "clinic.patient_results_viewed": z.object({
        viewed_by: z.string(),
        patient_id: z.string().uuid(),
        draw_count: z.number(),
    }),
    // A staff member opening an uploaded lab report PDF; the käyttöloki row for it
    // is written by the content read itself. No filename, no content.
    "clinic.lab_report_opened": z.object({ patient_id: z.string().uuid() }),
    // A staff member opening one questionnaire supplement photo; the käyttöloki
    // row is written by the content read itself. No question key, same rule as
    // clinic.patient_questionnaire_section_opened: which question carries a
    // photo says what the client answered.
    "clinic.questionnaire_photo_opened": z.object({ patient_id: z.string().uuid() }),
    // The Screener tab's read of a client's questionnaire answers — the
    // access_log("patient_questionnaire") disclosure surfaced as an event.
    // Counts and lifecycle only: no question key, no answer value, no section
    // the answers came from. Same rule as clinic.patient_results_viewed.
    "clinic.patient_questionnaire_viewed": z.object({
        viewed_by: z.string(),
        patient_id: z.string().uuid(),
        status: z.enum(["not_started", "in_progress", "submitted"]),
        answered_count: z.number(),
    }),
    // A UI interaction on data already fetched, not a second disclosure — the
    // whole questionnaire arrived in the one read the Screener pane made on
    // open. Carries which section of a fixed, public instrument was expanded;
    // never a question key, an answer, or anything derived from one.
    "clinic.patient_questionnaire_section_opened": z.object({
        viewed_by: z.string(),
        patient_id: z.string().uuid(),
        section_id: z.string(),
    }),
    // Staff unfolded one of the earlier draws under the trend chart. Browser-
    // reported and position-only, exactly like clinic.patient_note_revision_viewed:
    // every draw already reached the browser under the single
    // clinic.patient_results_viewed read above, so this is a UI interaction
    // rather than a second disclosure, and it carries how far back staff
    // reached (0 = the draw before the latest) and no value, unit, analyte
    // code or collection date. It is here because "does anyone open the older
    // draws" is what decides whether the section earns its place in a 320px
    // pane.
    "clinic.patient_earlier_draw_opened": z.object({
        patient_id: z.string().uuid(),
        draw_index: z.number(),
    }),
    /* The Journey tab's read. `stage` is a position in the care programme
       derived from counts and dates — no measured value can reach it (see
       apps/api/src/modules/journey/, and journey-purity.test.ts's import
       guard) — so unlike a lab value it is safe to carry here, and it is the
       one field that makes the event answer anything. Still no dates, and no
       declaration choice: which rung a customer is on is what we need to know,
       not what they chose to do about it. */
    "clinic.patient_journey_viewed": z.object({
        viewed_by: z.string(),
        patient_id: z.string().uuid(),
        stage: z.string(),
    }),
    // Which fields changed, never their values — see the no-PII rule above.
    "clinic.patient_contact_updated": z.object({
        patient_id: z.string().uuid(),
        fields: z.array(z.enum(["preferred_name", "email", "phone"])),
    }),
    "clinic.patient_contact_update_failed": z.object({
        patient_id: z.string().uuid(),
        reason: z.string(),
    }),
    // Dev/staging only (apps/api/src/modules/patients/routes.ts gates the
    // route itself out of production) — staff forcing a customer back into
    // onboarding to retest the flow. No answer values, just that it happened.
    "clinic.patient_onboarding_reset": z.object({
        patient_id: z.string().uuid(),
    }),
    // The Notes tab's read of the operational notes staff have written about
    // a customer — the access_log("patient_notes")/'read' disclosure surfaced
    // as an event. Count only: a note is free text a staff member wrote about
    // a named person, so none of its content may reach the analytics stream.
    "clinic.patient_notes_viewed": z.object({
        viewed_by: z.string(),
        patient_id: z.string().uuid(),
        note_count: z.number(),
    }),
    // A staff member added an operational note — the
    // access_log("patient_notes")/'create' write surfaced as an event. Length
    // rather than content, for the same reason as above; it is here because
    // "are notes actually being written, and are they one-liners or handover
    // paragraphs" is the question that tells us whether the tab works.
    "clinic.patient_note_added": z.object({
        patient_id: z.string().uuid(),
        body_length: z.number(),
    }),
    // Browser-reported, mirroring clinic.patient_contact_update_failed: the
    // server never sees the ones that failed to reach it.
    "clinic.patient_note_add_failed": z.object({
        patient_id: z.string().uuid(),
        reason: z.string(),
    }),
    // Staff loaded an earlier revision of a note into the field from the
    // revision dropdown. Browser-reported and position-only: the whole history
    // already reached the browser under one clinic.patient_notes_viewed, so
    // this is a UI interaction rather than a second disclosure, and it carries
    // how far back they reached (0 = newest) rather than any content. It is
    // here because "does anyone read the older revisions" is what decides
    // whether the dropdown earns its place.
    "clinic.patient_note_revision_viewed": z.object({
        patient_id: z.string().uuid(),
        revision_index: z.number(),
    }),
    // Surface name only, no clinical content — see §6's "name events at the
    // surface, not the diagnosis". Details and Notes read real data (each an
    // access_log row, tracked separately by clinic.patient_details_viewed and
    // clinic.patient_notes_viewed); Journey is still empty, a pure UI
    // interaction with no client data read.
    "clinic.workspace_tab_selected": z.object({
        tab: z.enum(["labs", "details", "journey", "notes", "screener"]),
    }),
    // Which queue chip staff picked. Browser-reported, filter name only — same
    // shape and reason as clinic.workspace_tab_selected: no patient id, no
    // count, just which view staff chose.
    "clinic.queue_filter_selected": z.object({
        filter: z.string(),
    }),
    // Where the staff user reached the account/sign-out affordance. The rail is
    // hidden below 960px, so this is what tells us whether the mobile tab-bar
    // route is actually carrying sign-outs. Browser-reported and deliberately
    // distinct from the server-confirmed auth.session_revoked.
    // A staff user followed a timeline entry's affordance into the context
    // pane — today only "Open in Labs" on a result entry. `kind` is the
    // timeline entry kind, a fixed vocabulary from the API contract: it says
    // which strand staff act on, and carries no value, unit, date or analyte
    // (docs/analytics-and-automation.md §3/§6). Deliberately distinct from
    // clinic.workspace_tab_selected, which the handler also emits: that one
    // answers "which tab do staff use", this one "does the feed send them
    // there".
    "clinic.timeline_entry_link_followed": z.object({
        kind: z.string(),
        target: z.enum(["labs"]),
    }),
    // A staff user clicked a row in the workspace's Plan strip to jump to it in
    // the feed. Kind only — never message text, timing or the client.
    "clinic.plan_row_opened": z.object({
        kind: z.enum(["scheduled_message", "action"]),
    }),
    // Staff opened the action list for a customer — the
    // access_log("patient_actions")/'read' disclosure surfaced as an event.
    // Count only: an action's title and instructions are free text a staff
    // member wrote about a named person and never reach the analytics stream.
    "clinic.patient_actions_viewed": z.object({
        viewed_by: z.string(),
        patient_id: z.string().uuid(),
        action_count: z.number(),
    }),
    // A staff member recorded a new action — the access_log("patient_actions")/
    // 'create' write surfaced as an event. Structure only (kind, where it was
    // started from, whether it carries a check-in, whether the client will see
    // it): "do staff start actions from a chat message or from the strip" is
    // what decides whether the message menu earns its place.
    "clinic.action_created": z.object({
        patient_id: z.string().uuid(),
        kind: actionKind,
        source: z.enum(["message", "plan"]),
        has_check_in: z.boolean(),
        client_visible: z.boolean(),
    }),
    // A staff member replaced an action with a successor. Whether a reason was
    // given, never the reason itself.
    "clinic.action_changed": z.object({
        patient_id: z.string().uuid(),
        kind: actionKind,
        has_check_in: z.boolean(),
        has_reason: z.boolean(),
    }),
    // A staff member recorded a check-in on an action. The outcome is the
    // clinician's own choice, never inferred; the note is free text and stays
    // out.
    "clinic.action_checked_in": z.object({
        patient_id: z.string().uuid(),
        outcome: z.enum(["continue", "stop", "complete"]),
    }),
    // Browser-reported: the action drawer opened, and from where. No patient id
    // and no content — it is a UI interaction, not a disclosure.
    "clinic.action_drawer_opened": z.object({
        source: z.enum(["message", "plan", "change"]),
    }),
    // Browser-reported, because the server never sees a save that failed to
    // reach it. `code` is the API error code (e.g. staff_profile_incomplete) or
    // `network`, never a message.
    "clinic.action_save_failed": z.object({
        code: z.string(),
    }),
    "clinic.account_sheet_opened": z.object({}),
    "clinic.signed_out": z.object({
        surface: signOutSurface,
    }),
    "clinic.chat_thread_opened": z.object({
        viewed_by: z.string(),
        thread_id: z.string(),
    }),
    // apps/web (ferrivia.fi) funnel — fills the gaps analytics/ga4/PLAN.md §B2
    // flags on the current WordPress site (view_item never fires,
    // begin_checkout doesn't exist at all). Every navigation fires the same
    // web.page_viewed event; content_type/slug/topic distinguish the page
    // instead of splitting into per-type events, keeping the dashboard query
    // shape flat. Slugs/paths only, no free text — same no-PII rule as every
    // other namespace in this file.
    "web.page_viewed": z.object({
        path: z.string(),
        content_type: z.enum(["page", "article", "product", "service", "event"]).optional(),
        slug: z.string().optional(),
        topic: z.string().optional(),
    }),
    // addon_selected predates addon_lookup_keys and is kept so the existing
    // series stays continuous -- it is now simply `addon_lookup_keys` non-empty.
    // Both are absent for the external-checkout CTA, which has no add-ons.
    "web.checkout_started": z.object({
        slug: z.string(),
        addon_selected: z.boolean().optional(),
        addon_lookup_keys: z.array(z.string()).optional(),
    }),
    // Sent through getTracker() like every other apps/web event — it used to
    // take a bespoke id-less path because the tracker was unavailable before
    // consent, which is no longer true (first-party events run on legitimate
    // interest, with a measurement opt-out). Recording only "accepted" would
    // make the rate meaningless, rejects being invisible by construction, and
    // this is a fact about the banner interaction itself, not personal
    // tracking.
    "web.consent_decided": z.object({
        accepted: z.boolean(),
        // Optional so rows already collected before the marketing category
        // shipped stay valid against this schema.
        marketing: z.boolean().optional(),
    }),
    // No PII (name/email/message text) -- same rule as every other event in
    // this namespace. Just a fact that a submission happened.
    "web.contact_submitted": z.object({}),
    // The site's conversion events, fired from the thank-you page MailerLite
    // redirects to. First-party and nothing else: no advertising platform
    // receives a copy, which is a regulatory position for a healthcare
    // operator rather than a configuration choice (platform/docs/
    // marketing-tags.md). `slug` is the thank-you page (e.g. "kiitoshki"),
    // which is what identifies the registration; no form contents travel with
    // either event, same no-PII rule as the rest of this namespace.
    "web.registration_completed": z.object({ slug: z.string() }),
    "web.lead_submitted": z.object({ slug: z.string() }),
    // Restores the WordPress-era GTM tags `cta_click_measure`/`cta_click_help`,
    // which fired on CSS selectors matching theme markup that no longer exists
    // after the migration to apps/web. One event with an `action` rather than
    // two per-button events, matching web.page_viewed's flat-shape precedent
    // above. `location` names the surface (e.g. "home_hero"), not a product or
    // topic -- same no-PII rule as the rest of this namespace. apps/web-us (the
    // US pilot landing page) emits it too, with `action: "join"` for its three
    // sign-up links (`letter_cta`, `how_it_works_cta`, `closing_cta`).
    "web.cta_clicked": z.object({
        location: z.string(),
        action: ctaAction,
    }),
    // Fires once per real transition (first establish, or re-establishing
    // after ended_at), not on every client-app bootstrap call — the
    // idempotent /me/patient-relationship endpoint is a no-op on repeat calls.
    "patient.relationship_established": z.object({
        user_id: z.string(),
    }),
    // Browser-reported onboarding funnel, deliberately kept distinct from the
    // server-confirmed onboarding.profile_completed below — the same split
    // already used for signin.* vs auth.* and chat.* vs its browser events.
    //
    // `resumed` is the drop-off instrument: answers are saved per step and
    // server-side, so a client can leave and come back on another device, and
    // this is what separates a fresh start from a continuation.
    "onboarding.started": z.object({ resumed: z.boolean() }),
    "onboarding.step_viewed": z.object({ step: onboardingStep }),
    "onboarding.step_completed": z.object({ step: onboardingStep }),
    "onboarding.step_back": z.object({ from_step: onboardingStep, to_step: onboardingStep }),
    "onboarding.skipped": z.object({}),
    // reason is a code, never an answer or a message body.
    "onboarding.save_failed": z.object({ step: onboardingStep, reason: z.string() }),
    "onboarding.completed": z.object({ duration_ms: z.number() }),
    // Server-confirmed completion of the client onboarding flow.
    //
    // No answer values in any onboarding event, here or above, and none
    // coming: reproductive life stage is GDPR Art. 9 data concerning health,
    // where the legitimate-interest basis this whole stream rests on is not
    // available. That rules out the answers themselves, and also a *count* of
    // selected life stages — nought-versus-three is a weak but real signal
    // about reproductive status. Step ids and durations only.
    //
    // One residual, accepted knowingly: step_viewed { step: "life_stage" }
    // only fires for clients who answered female, so its presence implies that
    // answer. Biological sex is not itself an Art. 9 special category, and
    // numbering the steps instead would make the funnel unreadable.
    //
    // `skipped` is the non-prod escape hatch (see the ENVIRONMENT check in
    // modules/onboarding/routes.ts), and it is here so a dev/staging funnel
    // never gets read as if it were prod behaviour.
    "onboarding.profile_completed": z.object({
        user_id: z.string(),
        skipped: z.boolean(),
    }),
    // FTN sign-in derived the birth year from the HETU rather than the client
    // answering. No HETU, no year — same no-PII / no-answer-values rules as
    // every event above.
    "onboarding.birth_year_derived": z.object({ user_id: z.string() }),
    // No message content, no free text, ever — same no-PII rule as every
    // other namespace in this file.
    "chat.message_created": z.object({
        thread_id: z.string(),
        author_type: chatAuthorType,
        block_count: z.number(),
    }),
    // The SLA instrument: seconds from the oldest unanswered client message to
    // this staff reply. Absent when the staff message opens the conversation.
    "chat.staff_replied": z.object({
        thread_id: z.string(),
        reply_latency_seconds: z.number().optional(),
    }),
    "chat.thread_status_changed": z.object({
        thread_id: z.string(),
        status: chatThreadStatus,
        changed_by: z.string(),
    }),
    // Coach-authored one-off scheduled sends, not the automations engine. Ids,
    // enums and numbers only: no message text, ever.
    "chat.message_scheduled": z.object({
        scheduled_message_id: z.string(),
        thread_id: z.string(),
        offset_hours: z.number().int(),
        if_no_reply: z.enum(["none", "remind", "nudge"]),
        reply_by_days: z.number().int().nullable(),
    }),
    "chat.scheduled_message_edited": z.object({
        scheduled_message_id: z.string(),
        thread_id: z.string(),
        was_held: z.boolean(),
        send_at_changed: z.boolean(),
        follow_up_changed: z.boolean(),
    }),
    // A client message overtook a scheduled one; it now waits for the author.
    "chat.scheduled_message_held": z.object({
        scheduled_message_id: z.string(),
        thread_id: z.string(),
    }),
    "chat.scheduled_message_cancelled": z.object({
        scheduled_message_id: z.string(),
        thread_id: z.string(),
        was_held: z.boolean(),
        by_author: z.boolean(),
        reason: z.enum(["staff", "patient_ended"]),
    }),
    // lateness_seconds is how far past send_at a sweep delivered it; null for
    // send_now, where there is no schedule to be late against.
    "chat.scheduled_message_sent": z.object({
        scheduled_message_id: z.string(),
        thread_id: z.string(),
        trigger: z.enum(["schedule", "send_now"]),
        was_held: z.boolean(),
        lateness_seconds: z.number().int().nullable(),
    }),
    "chat.nudge_sent": z.object({
        scheduled_message_id: z.string(),
        thread_id: z.string(),
    }),
    // Browser-reported, distinct from the server-confirmed chat.* events above
    // (same auth.* vs signin.* split already used elsewhere in this catalog).
    "chat.screen_viewed": z.object({}),
    "chat.send_attempted": z.object({}),
    // reason is a code, never a body — no message text in any property, ever.
    "chat.send_failed": z.object({
        reason: z.string(),
    }),
    // Reload-to-update banner (client + clinic web apps). No PII: build SHAs
    // and env only.
    "app.update_prompt_shown": z.object({
        from_version: z.string(),
        to_version: z.string(),
    }),
    "app.update_applied": z.object({
        from_version: z.string(),
        to_version: z.string(),
    }),
    "app.update_dismissed": z.object({
        from_version: z.string(),
        to_version: z.string(),
    }),
    // Install nudge (A2HS) on the client PWA login screen. No PII: platform
    // class and display mode only.
    "app.install_prompt_shown": z.object({ platform: installPlatform }),
    "app.install_prompt_dismissed": z.object({ platform: installPlatform }),
    "app.install_accepted": z.object({ platform: installPlatform }),
    "app.install_instructions_shown": z.object({}),
    "app.install_completed": z.object({ platform: installPlatform }),
    // On iOS there is no appinstalled event and no way to observe an A2HS, so a
    // session launched in "standalone" is the only evidence an install happened.
    "app.launched": z.object({ display_mode: displayMode }),
    // Client app shell only, driven by packages/ui's useOnline. No properties —
    // there is nothing about a connectivity blip worth a payload.
    "app.went_offline": z.object({}),
    "app.came_online": z.object({}),
    /* Client PWA bottom tab bar. Both paths are surface names, not clinical
       content (§3/§6): a route is where the client is standing, never what they
       are being treated for, so no value, biomarker or result can reach this
       stream through it.
  
       Emitted from one place (apps/client/src/app/TabNavigation.tsx) watching
       the router's location, rather than from the tab bar's click handler, so a
       back gesture and an in-app link are counted as honestly as a tap. */
    "nav.tab_changed": z.object({
        from: clientTab,
        to: clientTab,
        method: tabChangeMethod,
    }),
    // Tapping the tab you are already on, which scrolls that panel back to the
    // top. Only fires when there was something to scroll, so it measures the
    // affordance rather than counting no-ops.
    "nav.tab_reselected": z.object({ tab: clientTab }),
    // Surface-level only: block ids and stage names carry no clinical meaning
    // (docs/analytics-and-automation.md §5) — no value, no delta, no
    // biomarker name in either property.
    "home.surface_served": z.object({
        stage: z.string(),
        phase: z.string(),
        block_ids: z.array(z.string()),
    }),
    /* Every home block CTA, before it acts — an engine event, kept while home
       carries only notifications. `block_id` is normally the block's own id; a
       block with more than one way out qualifies it with the POSITION of the
       one taken (`<block>.step_1`) — never with what the destination is about.
       An article's topic id here would say something about this client's
       condition by implication, which is the same line the symptom cluster
       below draws. */
    "home.block_cta_clicked": z.object({
        block_id: z.string(),
    }),
    /* The home chooser's two cards (pathChooser block). Its own event rather
       than a block_cta_clicked, since the block has two CTAs and which one is
       the whole point. Which path was tapped, never why. */
    "home.path_chosen": z.object({
        path: z.enum(["support", "infusion"]),
    }),
    /* A distinct outcome from the CTA above, hence its own name rather than
       another block_id on home.block_cta_clicked. Browser-reported, and carries
       no claim about what dismissing meant — there is nothing to report beyond
       which card was put down (home_block_dismissals's migration comment). */
    "home.block_dismissed": z.object({
        block_id: z.string(),
    }),
    /* The client PWA's profile menu — the top-right sheet on home that carries
       sign-out and the build hash. Surface-level only, no identity in either
       payload: who signed out is already the envelope's business, and this
       namespace exists to answer "is the menu findable, and is it what people
       leave through" rather than to describe a person.
  
       Deliberately not folded into clinic.signed_out's `surface` enum: that
       event is the staff console's, and the two apps' sign-out affordances have
       nothing to do with each other. Both stay browser-reported and distinct
       from the server-confirmed auth.session_revoked. */
    "profile.menu_opened": z.object({}),
    "profile.signed_out": z.object({}),
    // The client editing their own contact details from the profile sheet —
    // no patient_id, unlike clinic.patient_contact_updated: the actor and the
    // subject are the same person, already identified by the envelope, so
    // naming them again here would be redundant rather than informative.
    "profile.details_updated": z.object({
        fields: z.array(z.enum(["preferred_name", "email", "phone"])),
    }),
    // Browser-reported, mirroring clinic.patient_contact_update_failed: the
    // server never sees the ones that failed to reach it.
    "profile.details_update_failed": z.object({
        reason: z.string(),
    }),
    // The profile sheet's language switcher — an explicit choice, distinct
    // from the passive server-locale sync `useLocaleSync` does at boot (which
    // isn't a user action and isn't tracked). `locale` is a UI preference, not
    // health data, so it's fine here unlike a symptom or a journey choice.
    "profile.language_changed": z.object({
        locale: z.enum(["fi", "en"]),
    }),
    // Client-declared journey choices. `kind` only, never `choice`: an
    // intent-to-raise-iron answer is health data (GDPR Art. 9) by the same
    // reasoning that keeps onboarding answers out of this catalog (see the
    // onboarding cluster comment below).
    "journey.declaration_created": z.object({ kind: z.string() }),
    /* The iron-infusion journey (platform/docs/infusion-journey.md).
  
       `interest` names which funnel a lead came through, not anything they told
       us about themselves — the lead form's own answers never reach this
       stream, and neither does an email or a name. That someone enquired about
       an infusion is health-adjacent, so this cluster stays at the same
       surface level as the rest of the catalog: which journey, from which
       source, and nothing about the person on it.
  
       The infusion milestones (booked, attended, infused) are declarations and
       go through journey.declaration_created above — kind only, like every
       other declaration. There is deliberately no infusion.* event per
       milestone: that would name a clinical act in the telemetry log.
       infusion.step_cta_clicked below covers UI interactions on the Iron
       infusion tab only — which button, never what the client has done. */
    // `cta` is the button, not the step's state: labs_buy/labs_upload on the
    // labs step, screener, booking (opens the explainer sheet) and book (the
    // sheet's outbound Sydänkeskus link).
    "infusion.step_cta_clicked": z.object({
        cta: z.enum(["labs_buy", "labs_upload", "screener", "booking", "book"]),
    }),
    // The Asiantuntijatuki tab, same rule as above — which button, never what
    // the client has done: purchase is the order button in the offer that heads
    // the steps card (shown only while unpaid) and opens the initial-assessment
    // sheet; labs_buy/labs_upload the lab package and the self-report form on
    // the labs step, labs_find the US sheet listing where labs can be ordered,
    // screener the questionnaire, book the outbound link to the coach's Google
    // Calendar booking page.
    "support.step_cta_clicked": z.object({
        cta: z.enum(["purchase", "labs_buy", "labs_upload", "labs_find", "screener", "book"]),
    }),
    "lead.captured": z.object({
        source: z.string(),
        interest: z.string(),
    }),
    "lead.capture_failed": z.object({
        source: z.string(),
        error: z.string(),
    }),
    "lead.converted": z.object({
        source: z.string(),
        interest: z.string(),
    }),
    "journey.track_assigned": z.object({
        track: z.string(),
        source: z.string(),
    }),
    /* The pre-appointment screener. Lifecycle only, and today that is all there
       is to report: a submission carries no answers until the questionnaire
       engine lands. When it does, what may be added here is completion and
       drop-off — a step id at most, never an answer. */
    "screener.started": z.object({ instrument: z.string() }),
    "screener.submitted": z.object({ instrument: z.string() }),
    /* The questionnaire summary ("Yhteenveto esitiedoistasi",
       platform/docs/questionnaire-summary.md). Ids, enums and counts only: never
       an answer, a slider value, a symptom id, an analyte name or a date. No
       count here is a gradient of health (symptom_count was rejected on
       2026-08-31). summary.created and summary.viewed are server-side; the rest
       are browser-reported. */
    "summary.created": z.object({
        instrument: z.string(),
        version: z.number().int(),
        trigger: z.enum(["submit", "backfill"]),
    }),
    "summary.viewed": z.object({ instrument: z.string() }),
    "summary.opened": z.object({
        source: z.enum(["home", "infusion_step", "support_step", "summary_list", "submitted"]),
    }),
    // A tap on a lab reading in the summary's labs part opens its explainer.
    // The code identifies the measurement, never its value.
    "summary.analyte_opened": z.object({ analyte_code: z.string() }),
    // section_id is a fixed enum of reader sections, never a question key.
    "summary.disclosure_toggled": z.object({
        section_id: summarySectionId,
        expanded: z.boolean(),
    }),
    "summary.trend_draw_selected": z.object({ draw_count: z.number().int() }),
    // Never the slot or article id: a routed id would reveal a diagnosis or a
    // pregnancy.
    "summary.article_clicked": z.object({ slot_kind: z.enum(["fixed", "routed"]) }),
    "summary.cta_clicked": z.object({ path: z.enum(["infusion", "support"]) }),
    /* The client's "Suunnitelmasi" card on Home: the care team's actions marked
       "shown in app" (GET /client/plan). Structure only — how many items and
       which kind was opened — never a title or instruction. plan.viewed is
       server-side, on every fetch; plan.item_opened is browser-reported. */
    "plan.viewed": z.object({ action_count: z.number().int().nonnegative() }),
    // surface is an enum of one so a later plan view can be told apart.
    "plan.item_opened": z.object({ kind: actionKind, surface: z.enum(["home"]) }),
    /* The questionnaire engine and the wellbeing analysis report
       (platform/docs/wellbeing-analysis.md). Same gap-8 rule as
       symptoms.checkin_created above: step ids and counts only, never an
       answer's value or a report's words. answered_count is a completion
       metric (how far through the instrument), not a gradient of health
       status the way a symptom_count would be -- that distinction is what
       makes it permitted where symptom_count was rejected (2026-08-31). */
    "questionnaire.answers_saved": z.object({
        instrument: z.string(),
        version: z.number().int(),
        answered_count: z.number().int(),
    }),
    // The questionnaire UI's own funnel, starting here with consent. A section
    // id at most, never an answer -- same ceiling as answers_saved above.
    // document_version is a property of our copy ("v1"), identical for every
    // client, not anything the client typed.
    "questionnaire.consent_granted": z.object({ instrument: z.string(), document_version: z.string() }),
    // section_id is a fixed constant from INSTRUMENT_DEFINITIONS, identical
    // for every client -- same ceiling as consent_granted above -- or
    // "symptom_check" for the required symptom step, which is not an instrument
    // section (@ferrivia/questionnaire-ui's SYMPTOM_STEP_ID). Saving that step is
    // covered by the symptoms.* events.
    // missing_shown: the section was reopened with reachable questions still
    // unanswered, so each one was marked "Vastaamatta" -- whether that marker
    // gets clients to close the gap. A form-progress fact; which question was
    // missing is never sent.
    "questionnaire.section_opened": z.object({
        instrument: z.string(),
        version: z.number().int(),
        section_id: z.string(),
        missing_shown: z.boolean(),
    }),
    // A questionnaire.step_viewed event lived here until 2026-09-15, when the
    // per-question step flow it fired from was cut in favour of one page per
    // section -- section_opened alone is now the per-section funnel signal.
    //
    // reason is an error code (network, invalid_answer, consent_required,
    // already_submitted), mirroring onboarding.save_failed -- never the
    // rejected value, never the question key that was rejected.
    "questionnaire.save_failed": z.object({ instrument: z.string(), reason: z.string() }),
    // A questionnaire.review_viewed event lived here until 2026-09-14, when
    // the review screen it fired from was cut -- the client now sends from the
    // hub, so there is no separate step left to view.
    //
    // Fired when the client leaves before submitting (today, only the hub's
    // close button -- a section has no exit of its own, only a path back to the
    // hub). answered_count is the same completion metric as answers_saved's;
    // section_id is null whenever the client wasn't inside a section at the
    // moment of exit, never an answer's value.
    "questionnaire.abandoned": z.object({
        instrument: z.string(),
        version: z.number().int(),
        answered_count: z.number().int(),
        section_id: z.string().nullable(),
    }),
    // SupplementPicker's own selection event (`iron_supplements.name`,
    // `other_supplement_items.name`). No `product_id` and no `name` -- either
    // one is an answer value on an Art. 9 health question (root CLAUDE.md,
    // "the software does not interpret"; REQ-MDR-002/005). from_catalog is
    // the only signal: whether the client picked a curated row or typed their
    // own text via "Ei listalla".
    "questionnaire.supplement_picked": z.object({
        instrument: z.string(),
        from_catalog: z.boolean(),
    }),
    // The "En ole varma, mikä näistä" link under a choice question, which opens
    // a sheet describing each option (contraceptives_details). question_key is
    // a fixed constant from INSTRUMENT_DEFINITIONS, never an answer.
    "questionnaire.option_guide_opened": z.object({
        instrument: z.string(),
        question_key: z.string(),
    }),
    // The full-screen item editor's commit and delete (`iron_supplements`,
    // `other_supplement_items`, `iron_infusion_items`). question_key is a
    // fixed constant from INSTRUMENT_DEFINITIONS -- same ceiling as
    // section_opened's section_id. Never a field value, never a field key.
    "questionnaire.item_saved": z.object({
        instrument: z.string(),
        version: z.number().int(),
        question_key: z.string(),
        mode: z.enum(["added", "edited"]),
    }),
    "questionnaire.item_removed": z.object({
        instrument: z.string(),
        version: z.number().int(),
        question_key: z.string(),
    }),
    // Supplement-label photos on a `photo_upload` field
    // (platform/docs/questionnaire-photos.md). Stored and shown as uploaded,
    // never read by software, so nothing about the image appears here: no id,
    // size, filename or count. question_key is a fixed constant from
    // INSTRUMENT_DEFINITIONS, same ceiling as item_saved. uploaded is the
    // server-confirmed write; upload_failed and removed are browser-reported.
    "questionnaire.photo_uploaded": z.object({ instrument: z.string(), question_key: z.string() }),
    "questionnaire.photo_upload_failed": z.object({
        instrument: z.string(),
        reason: z.enum(["not_image", "too_large", "limit_reached", "network", "unknown"]),
    }),
    "questionnaire.photo_removed": z.object({ instrument: z.string(), question_key: z.string() }),
    // An answer carried over from onboarding and shown preselected
    // (today only `female_reproductive_stage`, from the onboarding life stage),
    // then the client's first change to it. Together they say how often the
    // carried-over answer was stale. question_key is a fixed constant from
    // INSTRUMENT_DEFINITIONS -- never the prefilled value, never the new one.
    "questionnaire.answer_prefilled": z.object({
        instrument: z.string(),
        version: z.number().int(),
        question_key: z.string(),
    }),
    "questionnaire.prefilled_answer_changed": z.object({
        instrument: z.string(),
        version: z.number().int(),
        question_key: z.string(),
    }),
    "analysis.requested": z.object({}),
    "analysis.published": z.object({
        version: z.number().int(),
        section_count: z.number().int(),
    }),
    "analysis.call_held": z.object({}),
    /* Self-reported symptom check-ins.
  
       Nothing here describes what was reported. A symptom id, the free-text
       body, a slider value — or a count of how many symptoms were ticked —
       would put Art. 9 clinical content into this stream, which is the no-PII
       rule every other namespace here follows and §3/§6's "name events at the
       surface, not the diagnosis".
  
       A symptom_count property was considered and rejected (Ville, 2026-08-31):
       a bare count with no symptom identity looks like chat.message_created's
       block_count, but "this client reported seven symptoms" is a gradient of
       health status in a way that a message's block count is not. Do not add it
       back. This also means the catalog deliberately does NOT follow
       product/prototypes/client-journey.md's proposed "symptom entry saved
       (with symptom ids)".
  
       The checklist properties are absent rather than null on a quick
       check-in, which has no checklist to describe. */
    "symptoms.checkin_created": z.object({
        kind: symptomCheckinKind,
        none_of_these: z.boolean().optional(),
        has_free_text: z.boolean().optional(),
        checklist_version: z.number().optional(),
    }),
    // Browser-reported funnel, deliberately distinct from the server-confirmed
    // event above — a client can open the sheet, expand it and abandon it
    // without the server ever seeing a check-in.
    "symptoms.sheet_opened": z.object({ surface: z.enum(["home"]) }),
    // Client-submitted lab results. No values, no biomarker names — same
    // no-PII/no-clinical-content rule as every other namespace in this file.
    "results.upload_sheet_opened": z.object({}),
    "results.upload_saved": z.object({}),
    "results.save_failed": z.object({ reason: z.string() }),
    "results.report_upload_started": z.object({}),
    "results.report_upload_failed": z.object({
        reason: z.enum(["not_pdf", "too_large", "network", "unknown"]),
    }),
    "results.report_opened": z.object({}),
    // Server-confirmed write, distinct from the browser-reported funnel above:
    // a client can save the sheet and still hit a failed request. analyte_codes
    // is the boundary of what may appear here — no value, no unit, no date. A
    // value or a date would pull this under GDPR Art. 9 the way a symptom id
    // does for symptoms.checkin_created above; see that event's note.
    "labs.self_reported_result_created": z.object({
        analyte_codes: z.array(z.string()),
        analyte_count: z.number(),
        is_new_draw: z.boolean(),
        units: z.array(z.string()).optional(),
    }),
    // US lab report PDFs (platform/docs/lab-report-files.md). The file is stored
    // and shown as uploaded and never read by the software, so there is no
    // content, filename, size or date here -- only whether the upload was tied to
    // a result and who opened it.
    "labs.report_file_uploaded": z.object({ linked_to_result: z.boolean() }),
    "labs.report_file_viewed": z.object({ viewer: z.enum(["client", "staff"]) }),
    // Lab result ingest (platform/docs/lab-result-ingest.md). Emitted with
    // actor.type 'system': a lab pushing a result has no person behind it, and
    // naming one would put a fictional human in the record. Same
    // no-clinical-content boundary as the self-report event above — no value, no
    // unit, no collection date anywhere in this group.
    //
    // Fires on every claim, duplicates included, because redelivery volume is a
    // property of the integration worth being able to see.
    "labs.submission_received": z.object({
        lab_code: z.string(),
        kind: z.enum(["specimen_collected", "result_reported"]),
        duplicate: z.boolean(),
    }),
    // A draw we were told about. This is the notification the `sampled` journey
    // stage has always read and never had (see journey/spine.ts), so its arrival
    // is worth its own event rather than being folded into the result below.
    "labs.specimen_recorded": z.object({
        channel: z.string(),
        collection_method: z.string(),
    }),
    // Values stored. skipped_analyte_count > 0 means the lab reported an analyte
    // our catalog has no entry for: the known values were stored anyway and the
    // receipt names what was skipped, so a non-zero count here is the signal to
    // add it to LAB_ANALYTES.
    "labs.reported_result_created": z.object({
        panel_code: z.string().nullable(),
        analyte_codes: z.array(z.string()),
        analyte_count: z.number(),
        skipped_analyte_count: z.number(),
    }),
    // Told, understood, and not attachable *yet* — `unclaimed_order` is the one
    // to watch: it is the unclaimed-account problem showing up as traffic rather
    // than as a hypothesis.
    "labs.submission_unmatched": z.object({ reason: z.string() }),
    // Told, understood, and refused. A code, never a message.
    "labs.submission_rejected": z.object({ reason: z.string() }),
    // Server-confirmed read of the Results tab's own data. draw_count is a
    // count of results, same no-value boundary as the write-path event above —
    // it says how much there is to see, not what it says.
    "results.viewed": z.object({ draw_count: z.number() }),
    // Client-side: a fact-card was tapped/opened. analyte_code identifies which
    // row, never its value — same boundary as analyte_codes above.
    "results.analyte_opened": z.object({ analyte_code: z.string() }),
    // Client-side: the "add older results" button under the lab history list.
    // Opens the same upload sheet as results.upload_sheet_opened; kept separate
    // so we can tell whether the history list is what brings older labs in.
    "results.add_older_clicked": z.object({}),
    // Client-side: the PVK panel was folded open or shut. analyte_count is how
    // many sub-measurements the panel holds — a size, not a reading; no code,
    // no value, no unit.
    "results.pvk_toggled": z.object({ expanded: z.boolean(), analyte_count: z.number() }),
    // Client-side: the reader moved the trend figure's readout to another draw.
    // Emitted once per visit to the Results tab, not once per column crossed —
    // the question is whether the figure gets used at all. draw_count is how
    // much history was on screen, the same size-not-content boundary as
    // results.viewed above: no value, no unit, no analyte, no date. There is
    // deliberately no matching "trend shown" event — the figure renders at two
    // or more draws, so results.viewed's draw_count already says whether it
    // appeared.
    "results.trend_draw_selected": z.object({ draw_count: z.number() }),
    "symptoms.full_check_opened": z.object({}),
    "symptoms.save_attempted": z.object({ kind: symptomCheckinKind }),
    "symptoms.save_failed": z.object({ kind: symptomCheckinKind, reason: z.string() }),
    // Emitted once per hourly reconcile run (not per webhook delivery — that
    // would be one event every product/price edit, which tells no one
    // anything the mirror tables don't already show). drift_count > 0 means
    // something present locally is no longer present upstream.
    "commerce.catalog_mirror_synced": z.object({
        livemode: z.boolean(),
        products_upserted: z.number(),
        prices_upserted: z.number(),
        drift_count: z.number(),
    }),
    // A persistently non-zero drift count is the signal that the catalog
    // webhook endpoint has stopped receiving deliveries — see
    // 012_catalog.sql's catalog_sync_state comment. Kept distinct from the
    // heartbeat above so an on-call alert can filter on this event alone.
    "commerce.catalog_drift_detected": z.object({
        livemode: z.boolean(),
        drift_count: z.number(),
    }),
    // Browser-reported, apps/web only -- ids only, never a product/panel name
    // (see the no-PII rule above; lookup_key is a SKU-shaped handle, not
    // clinical content).
    "commerce.cart_updated": z.object({
        lookup_key: z.string(),
        quantity: z.number(),
        action: cartAction,
    }),
    "commerce.checkout_session_created": z.object({
        order_id: z.string(),
        item_count: z.number(),
        total_cents: z.number(),
        currency: z.string(),
        is_gift: z.boolean(),
    }),
    "commerce.order_placed": z.object({
        order_id: z.string(),
        source: orderSource,
    }),
    // Emitted by the commerce webhook handler (a later phase) once an order's
    // status actually reaches 'paid' -- declared here now so the catalog is
    // the single place every commerce event is registered, even ahead of the
    // code that emits it.
    "commerce.order_paid": z.object({
        order_id: z.string(),
        total_cents: z.number(),
        currency: z.string(),
        is_new_customer: z.boolean(),
    }),
    // Mirrors orders.email_skipped for the storefront path -- lets the invite
    // funnel be segmented without joining back to Postgres.
    "commerce.invite_email_skipped": z.object({
        order_id: z.string(),
        reason: z.enum(["existing_customer", "work_domain", "gift"]),
    }),
    // The customer-support Slack alert for a returning customer buying labs
    // again on the storefront (the membership upsell prompt), emitted by the
    // commerce outbox once the message is posted. Ids and a count only, no
    // product and no channel text. Which orders qualify is decided at webhook
    // time (`isRepeatLabPurchase`), so "eligible but never alerted" is any
    // order.paid.repeat_purchase_alert outbox row with neither event.
    "commerce.repeat_purchase_alert_sent": z.object({
        order_id: z.string(),
        prior_paid_order_count: z.number(),
    }),
    // The same alert, let go on purpose: the buyer already bought the
    // membership within the last year (`active_membership`), or the
    // slack-message-webhook-url secret is still Terraform's "unset" placeholder
    // (`not_configured`).
    "commerce.repeat_purchase_alert_skipped": z.object({
        order_id: z.string(),
        reason: z.enum(["active_membership", "not_configured"]),
    }),
    // Booleans rather than a variant enum, because the blocks are independent
    // -- and this is the only way to later ask "how many buyers were told to
    // book a draw?" No PII: an order id and a handful of SKU-shaped routing
    // flags, no address, no product name, no measured value. `is_gift` is one
    // of them: a gift order gets the "we will contact the recipient" notice
    // instead of every block, so all three block flags read false there, and
    // this is what separates that from an order with no fulfilment copy at all.
    // Once per order, on its first send only, and event-desk orders included:
    // join `orders.source = 'event_pos'` to separate them from the storefront.
    "commerce.order_confirmation_email_sent": z.object({
        order_id: z.string(),
        has_lab_block: z.boolean(),
        has_analysis_block: z.boolean(),
        has_membership_block: z.boolean(),
        has_customer_id: z.boolean(),
        // Whether the pass could name which measurements the draw takes. False
        // on a lab-carrying order whose lines have no `lab_panel_code` -- the
        // membership bundle today -- which is a catalog-metadata gap that shows
        // up as a client arriving at the desk with numbers but no requisition
        // list. Worth counting rather than inferring from the product mix.
        has_measurement_list: z.boolean(),
        is_gift: z.boolean(),
    }),
    "commerce.order_confirmation_email_skipped": z.object({
        order_id: z.string(),
        reason: z.enum(["no_buyer_email"]),
    }),
    // Event-desk sales (staff-recorded at events, paid on MobilePay/cash). No
    // email, name or staff identity: an order id and non-PII routing facts.
    // `price_overridden` is the audit signal for staff-edited prices.
    "desk.sale_recorded": z.object({
        order_id: z.string(),
        lookup_keys: z.array(z.string()),
        item_count: z.number(),
        amount_cents: z.number(),
        catalog_amount_cents: z.number(),
        price_overridden: z.boolean(),
        has_pos_reference: z.boolean(),
        payment_method: z.enum(["mobilepay", "cash", "other"]),
        event_name: z.string(),
    }),
    "desk.pos_reference_added": z.object({
        order_id: z.string(),
    }),
    // A desk sale's automatic order confirmation (`trigger: "sale"`) or a
    // staff resend (`trigger: "resend"`) that the mail server refused or timed
    // out on. The sale itself is saved either way; this counts buyers left
    // without a confirmation until someone presses resend.
    "desk.confirmation_email_failed": z.object({
        order_id: z.string(),
        trigger: z.enum(["sale", "resend"]),
    }),
    // A staff-pressed confirmation send that went out. `first_send` is true
    // when the buyer had never received one (the automatic send failed, or
    // the sale predates it) -- that send also emits
    // `commerce.order_confirmation_email_sent`; a true resend does not.
    "desk.confirmation_email_resent": z.object({
        order_id: z.string(),
        first_send: z.boolean(),
    }),
    "desk.sale_rejected": z.object({
        reason: z.enum([
            "invalid_request",
            "unknown_price",
            "not_event_price",
            "addon_without_base",
            "unsupported_combination",
        ]),
    }),
    "commerce.order_payment_failed": z.object({
        order_id: z.string(),
    }),
    "commerce.order_refunded": z.object({
        order_id: z.string(),
        amount_cents: z.number(),
    }),
    "commerce.order_disputed": z.object({
        order_id: z.string(),
        amount_cents: z.number(),
    }),
    "commerce.order_fulfilled": z.object({
        order_id: z.string(),
    }),
    // Emitted by the commerce outbox dispatch route (Phase 4) once a claimed
    // outbox row's Glide projection succeeds -- once per row, covering
    // order.paid, order.paid.sale_rows and order.refunded alike. No PII:
    // event_type is a fixed string from the outbox's own check constraint,
    // never free text.
    "commerce.outbox_dispatched": z.object({
        order_id: z.string(),
        event_type: z.string(),
    }),
    // Emitted when a claimed outbox row's Glide projection fails permanently
    // (a non-retryable GlideCallError) and the row is marked 'dead'. Ops gets
    // a `sendAlertEmail` alongside this, and can replay the row with
    // `pnpm --filter @ferrivia/api outbox-replay`. No error text here on
    // purpose -- see `outbox.last_error` in Postgres for that.
    "commerce.outbox_dead_lettered": z.object({
        order_id: z.string(),
        event_type: z.string(),
    }),
    // The client PWA's in-app purchase (the iron panel sheet on the Iron
    // infusion tab; formerly the Store tab, whose event names these keep) --
    // ids only, never a product name (see the no-PII rule above; lookup_key is
    // a SKU-shaped handle).
    // `lookup_key` is the first line's key (the panel or service being bought);
    // `item_count` is how many lines went to Checkout, which is what the lab
    // page's add-on picker moves. `addon_included` predates the picker and is
    // kept rather than folded into the count so the existing series stays
    // continuous across this change -- it is now simply `item_count > 1`.
    "store.checkout_started": z.object({
        lookup_key: z.string(),
        addon_included: z.boolean(),
        item_count: z.number().int(),
    }),
    // reason is the ApiError code, or "network" for a transport failure --
    // never the raw error message, which could carry request-specific detail.
    "store.checkout_failed": z.object({ reason: z.string() }),
    // Fired once the buyer lands back in the app from Stripe Checkout with an
    // order/token pair (on any tab; the dialog is mounted in the app shell) --
    // before the order-status poll has a terminal answer.
    "store.order_confirmation_viewed": z.object({}),
    // Fired once when the order-status poll reaches a terminal state.
    "store.order_confirmation_resolved": z.object({ status: z.enum(["paid", "timed_out", "unsuccessful"]) }),
    // Which way a buyer left the paid-order confirmation. `book_draw` is the
    // outbound booking link the dialog offers when the order carries lab
    // measurements; `done` is dismissing the dialog, which leaves the buyer on
    // the tab they returned to. This is what says whether the
    // purchase-to-booking hand-off is working. A fulfilment
    // kind is a SKU-shaped routing label, not a measured value or a symptom.
    "store.order_confirmation_cta_clicked": z.object({
        cta: z.enum(["book_draw", "done"]),
        carries_lab_measurements: z.boolean(),
    }),
    /* One add-on ticked or unticked in the iron panel sheet. `lookup_key` is a
       SKU-shaped handle, never a test name -- same no-PII rule as the events
       above, and the same reason the store never reports anything measured:
       this says which product a client considered buying and nothing about
       their health. `item_count` is the whole panel's size after the change,
       so a session's shape is readable without replaying every toggle. */
    "store.lab_addon_toggled": z.object({
        lookup_key: z.string(),
        selected: z.boolean(),
        item_count: z.number().int(),
    }),
    /* The iron panel sheet's way out to a person. Which measurements someone should
       have is a clinical question the platform is not allowed to answer, so this
       CTA is the answer — and how often it is taken is capacity planning for the
       health team rather than a funnel metric. Distinct from the
       `nav.tab_changed` it also produces, which cannot say which of the app's
       several routes into chat was used. */
    "store.lab_help_chat_opened": z.object({}),
    // The order history opened. `source` is where from; the profile menu is
    // the only entry point since the Store tab (and its header button) went.
    "store.orders_opened": z.object({ source: z.enum(["profile_menu"]) }),
    // One order opened from the list. `has_lab_panel` separates the orders a
    // client opens to find a number for the laboratory from the ones they open
    // to read a receipt -- the thesis the order screen is built on.
    "store.order_detail_viewed": z.object({ has_lab_panel: z.boolean() }),
    // The order number copied from the detail screen. Nothing identifying: the
    // number itself is not sent, only that the gesture happened.
    "store.order_number_copied": z.object({}),
};
